import React from 'react';
import { Link } from 'react-router-dom';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import QuoteRequestSuccess, { QuoteSuccessPrimaryLink } from '../components/QuoteRequestSuccess';
import GoogleAddressAutocomplete from '../components/GoogleAddressAutocomplete';
import { filterHufDigitsInput } from '../utils/hufInput';
import { isValidFlexiblePhone, PHONE_FORMAT_MESSAGE, PHONE_PLACEHOLDER, PHONE_REQUIRED_MESSAGE } from '../utils/phoneValidation';
import { fetchDrivingDistanceKm } from '../utils/drivingKm';
import { computeIkeaEstimate, formatEstimateFt, normalizePricingSettings, pricingSnapshot } from '../utils/csomagStyleEstimate';
import usePricingSettings from '../hooks/usePricingSettings';
import {
  readFileAsBase64Payload,
  postQuoteInquiry,
  emailWarningsFromInquiryResponse,
} from '../utils/quoteInquiryApi';
import '../App.css';

const MAX_FILE_MB = 5;
const ACCEPT_EXT = ['.jpg', '.jpeg', '.png', '.pdf'];

const STORE_OPTIONS = [
  { value: '', label: '— Kérjük, válasszon egy opciót —' },
  { value: 'ikea_ors', label: 'IKEA Örs Vezér tere' },
  { value: 'ikea_soroksar', label: 'IKEA Soroksár' },
  { value: 'ikea_budaors', label: 'IKEA Budaörs' },
  { value: 'mobelix', label: 'Möbelix' },
  { value: 'xxxlutz', label: 'XXXLutz' },
  { value: 'egyeb', label: 'Egyéb (megjegyzésben)' },
];

/** IKEA áruházak induló címe (Routes API szöveges cím). Más áruháznál nincs automatikus km. */
const STORE_ROUTE_ORIGIN_ADDRESS = {
  ikea_ors: 'IKEA, Örs vezér tere 25, 1148 Budapest, Magyarország',
  ikea_soroksar: 'IKEA Soroksár, Bevásárló utca 2, 1239 Budapest, Magyarország',
  ikea_budaors: 'IKEA Budaörs, Kinizsi utca 1, 2040 Budaörs, Magyarország',
};

function validateFile(file) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  const ok = ACCEPT_EXT.some((ext) => lower.endsWith(ext));
  if (!ok) return 'Csak jpg, png vagy pdf tölthető fel.';
  if (file.size > MAX_FILE_MB * 1024 * 1024) return `Max. ${MAX_FILE_MB} MB.`;
  return null;
}

function parseWeightKg(value) {
  const n = Number(String(value || '').replace(',', '.').trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function filterWeightInput(raw) {
  const cleaned = String(raw ?? '').replace(/[^\d,.]/g, '').replace(',', '.');
  const [whole = '', fraction = ''] = cleaned.split('.');
  return fraction ? `${whole}.${fraction.slice(0, 2)}` : whole;
}

export default function IkeaHazhozszallitasQuotePage() {
  const { pricingSettings } = usePricingSettings();
  const effectivePricing = normalizePricingSettings(pricingSettings);
  const ikeaPricing = effectivePricing.ikea;
  const zoneOptions = ikeaPricing.zones;
  const valueSurchargeOptions = ikeaPricing.valueSurcharges;
  const collectionOptions = ikeaPricing.collectionFees;
  const STEPS = [
    { id: 'zone', title: 'Zónák és felár' },
    { id: 'details', title: 'Áruház, cél és bútor adatai' },
    { id: 'extras', title: 'Egyéb adatok' },
    { id: 'contact', title: 'Ajánlatkérő adatai' },
  ];

  const [step, setStep] = React.useState(0);
  const [stepError, setStepError] = React.useState('');
  const [invalidFieldId, setInvalidFieldId] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitEmailWarning, setSubmitEmailWarning] = React.useState('');
  const [submitPending, setSubmitPending] = React.useState(false);

  const [zone, setZone] = React.useState('');
  const [valueSurchargeValue, setValueSurchargeValue] = React.useState('');
  const [collectionValue, setCollectionValue] = React.useState('');

  const [store, setStore] = React.useState('');
  const [toAddressSearch, setToAddressSearch] = React.useState('');
  const [toZip, setToZip] = React.useState('');
  const [toCity, setToCity] = React.useState('');
  const [furnitureWeightKg, setFurnitureWeightKg] = React.useState('');
  const [furnitureValueFt, setFurnitureValueFt] = React.useState('');
  const [furnitureParams, setFurnitureParams] = React.useState('');
  const [note, setNote] = React.useState('');
  const [invoiceFile, setInvoiceFile] = React.useState(null);
  const [orderInfoFile, setOrderInfoFile] = React.useState(null);
  const [extraFile1, setExtraFile1] = React.useState(null);
  const [extraFile2, setExtraFile2] = React.useState(null);
  const [invoiceErr, setInvoiceErr] = React.useState('');
  const [orderErr, setOrderErr] = React.useState('');
  const [extraErr1, setExtraErr1] = React.useState('');
  const [extraErr2, setExtraErr2] = React.useState('');

  const [scheduleType, setScheduleType] = React.useState('flexible');
  const [flexFrom, setFlexFrom] = React.useState('');
  const [flexTo, setFlexTo] = React.useState('');
  const [fixDate, setFixDate] = React.useState('');

  const [insurance, setInsurance] = React.useState('');
  const [paymentWhen, setPaymentWhen] = React.useState('');

  const [routeDistanceKm, setRouteDistanceKm] = React.useState('');
  const [routeDistanceLoading, setRouteDistanceLoading] = React.useState(false);
  const [routeDistanceError, setRouteDistanceError] = React.useState('');
  const ikeaKmSeqRef = React.useRef(0);

  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailConfirm, setEmailConfirm] = React.useState('');
  const [privacyAccepted, setPrivacyAccepted] = React.useState(false);

  const furnitureWeightNumber = parseWeightKg(furnitureWeightKg);
  const price = computeIkeaEstimate({
    zone,
    weightKg: furnitureWeightNumber,
    valueSurchargeValue,
    collectionValue,
    store,
    settings: effectivePricing,
  });
  const totalFt = price.total;
  const totalDisplay = price.delivery.custom ? 'Egyedi ajánlat' : formatEstimateFt(totalFt);

  React.useEffect(() => {
    const origin = STORE_ROUTE_ORIGIN_ADDRESS[store];
    const dest = String(toAddressSearch || '').trim();
    if (!origin || !dest) {
      setRouteDistanceKm('');
      setRouteDistanceError('');
      setRouteDistanceLoading(false);
      return undefined;
    }
    const seq = Date.now() + Math.random();
    ikeaKmSeqRef.current = seq;
    setRouteDistanceLoading(true);
    setRouteDistanceError('');
    let cancelled = false;
    (async () => {
      try {
        const km = await fetchDrivingDistanceKm('', '', origin, dest);
        if (cancelled || ikeaKmSeqRef.current !== seq) return;
        setRouteDistanceKm(String(km));
      } catch (e) {
        if (cancelled || ikeaKmSeqRef.current !== seq) return;
        setRouteDistanceError(e.message || 'A távolságot most nem tudtuk kiszámolni.');
        setRouteDistanceKm('');
      } finally {
        if (!cancelled && ikeaKmSeqRef.current === seq) setRouteDistanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store, toAddressSearch]);

  const validateStep = (s) => {
    if (s === 0) {
      if (!zone) return 'Válassz zónát.';
      if (!furnitureWeightKg.trim()) return 'Add meg a szállítandó bútor súlyát.';
      if (furnitureWeightNumber <= 0) return 'A szállítandó bútor súlya csak pozitív szám lehet.';
      if (!valueSurchargeValue) return 'Válassz egy szállítmány érték felár opciót.';
      if (!collectionValue) return 'Válassz egy üzleten belüli összegyűjtés opciót.';
      return '';
    }
    if (s === 1) {
      if (!store) return 'Válaszd ki, melyik áruházból történik a szállítás.';
      if (!toZip.trim()) return 'Add meg az irányítószámot.';
      if (!toCity.trim()) return 'Add meg a települést.';
      if (!furnitureValueFt.trim()) return 'Add meg a szállítandó bútor értékét (bruttó).';
      if (!furnitureParams.trim()) return 'Add meg a szállítandó bútor paramétereit.';
      if (!scheduleType) return 'Válassz felvételi típust.';
      if (scheduleType === 'flexible') {
        if (!flexFrom || !flexTo) return 'Add meg a rugalmas felvételi idősávot (-tól / -ig).';
      } else if (!fixDate) return 'Add meg a fix felvételi napot.';
      if (invoiceErr) return invoiceErr;
      if (orderErr) return orderErr;
      if (extraErr1) return extraErr1;
      if (extraErr2) return extraErr2;
      return '';
    }
    if (s === 2) {
      if (insurance !== 'igen' && insurance !== 'nem') return 'Válaszd ki, hogy igényelsz-e biztosítást.';
      if (paymentWhen !== 'elore' && paymentWhen !== 'keszpenz') return 'Válaszd ki a fizetési módot.';
      return '';
    }
    if (s === 3) {
      if (!name.trim()) return 'Add meg a neved.';
      if (!phone.trim()) return PHONE_REQUIRED_MESSAGE;
      if (!isValidFlexiblePhone(phone)) {
        return PHONE_FORMAT_MESSAGE;
      }
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Érvényes e-mail címet adj meg.';
      if (email !== emailConfirm) return 'Az e-mail címek nem egyeznek.';
      if (!privacyAccepted) return 'Az adatkezelési tájékoztató elfogadása kötelező.';
      return '';
    }
    return '';
  };

  const scrollToInvalidField = (fieldId) => {
    if (!fieldId) return;
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.closest('label')?.querySelector('input, select, textarea') ?? el;
    focusable?.focus?.();
  };

  const next = () => {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      let fieldId = '';
      if (err.includes('fix felvételi napot')) fieldId = 'fixDate';
      else if (err.includes('rugalmas felvételi idősávot')) fieldId = 'flexFrom';
      else if (err.includes('bútor súlyát') || err.includes('súlya csak pozitív')) fieldId = 'ikeaWeightKg';
      else if (err.includes('irányítószámot')) fieldId = 'toZip';
      else if (err.includes('települést')) fieldId = 'toCity';
      else if (err.includes('áruházból')) fieldId = 'ikeaStore';
      else if (err.includes('bútor értékét')) fieldId = 'furnitureValue';
      else if (err.includes('bútor paramétereit')) fieldId = 'furnitureParams';
      else if (err.includes('felvételi típust')) fieldId = 'scheduleType';

      setInvalidFieldId(fieldId);
      setTimeout(() => scrollToInvalidField(fieldId), 60);
      return;
    }
    setStepError('');
    setInvalidFieldId('');
    setStep((x) => Math.min(x + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setStepError('');
    setInvalidFieldId('');
    setStep((x) => Math.max(x - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const err = validateStep(3);
    if (err) {
      setStepError(err);
      setInvalidFieldId('');
      return;
    }
    setStepError('');
    setInvalidFieldId('');

    const zoneLabel = zoneOptions.find((x) => x.value === zone)?.label || zone;
    const storeLabel = STORE_OPTIONS.find((x) => x.value === store)?.label || store;
    const vsOpt = valueSurchargeOptions.find((x) => x.value === valueSurchargeValue);
    const collOpt = collectionOptions.find((x) => x.value === collectionValue);
    const fromAddr = STORE_ROUTE_ORIGIN_ADDRESS[store] || storeLabel;
    const contentBody = [
      `Zóna: ${zoneLabel}`,
      `Súly: ${furnitureWeightKg} kg`,
      `Alap szállítási díj: ${price.delivery.custom ? price.delivery.label : `${price.delivery.label} - ${formatEstimateFt(price.delivery.fee)}`}`,
      vsOpt && `Érték szerinti felár: ${vsOpt.label}`,
      collOpt && `Üzleten belüli összegyűjtés: ${collOpt.label}`,
      price.budaors > 0 && `Budaörsi áruház felár: ${formatEstimateFt(price.budaors)}`,
      `Bútor paraméterek: ${furnitureParams}`,
      String(note || '').trim() && `Megjegyzés: ${String(note).trim()}`,
    ]
      .filter(Boolean)
      .join('\n');

    const filesInOrder = [invoiceFile, orderInfoFile, extraFile1, extraFile2].filter(Boolean);
    let attachmentUploads = [];
    try {
      attachmentUploads = await Promise.all(filesInOrder.map((f) => readFileAsBase64Payload(f)));
    } catch (readErr) {
      setStepError(readErr?.message || 'Nem sikerült beolvasni a csatolmányokat.');
      return;
    }
    const attachmentNames = filesInOrder.map((f) => f?.name).filter(Boolean);

    const estimatedNetForPayload = price.delivery.custom ? 0 : totalFt;
    const formatFt = formatEstimateFt;

    const shipLine = {
      type: 'IKEA házhozszállítás',
      packaging: storeLabel,
      content: contentBody,
      weightKg: furnitureWeightKg,
      lengthCm: '',
      widthCm: '',
      heightCm: '',
      note: '',
    };

    const payload = {
      inquiryType: 'ikea_hazhozszallitas',
      submittedAt: new Date().toISOString(),
      route: {
        fromAddress: fromAddr,
        toAddress: [toZip, toCity, toAddressSearch].filter((x) => String(x || '').trim()).join(', '),
        fromCity: '',
        fromZip: '',
        fromCountry: 'Magyarország',
        toCity,
        toZip,
        toCountry: 'Magyarország',
        distanceKm: routeDistanceKm || '',
      },
      shipment: {
        type: 'IKEA házhozszállítás',
        packaging: storeLabel,
        content: contentBody,
        weightKg: furnitureWeightKg,
        sizeCm: furnitureValueFt ? `Bútor érték (bruttó): ${furnitureValueFt} Ft` : '',
        note: '',
      },
      shipments: [shipLine],
      schedule: {
        deliveryMode: 'normal',
        schedule: scheduleType === 'flexible' ? `${flexFrom || ''} - ${flexTo || ''}` : fixDate,
        paymentWhen,
        currency: 'HUF',
      },
      insurance: {
        enabled: insurance === 'igen',
        shipmentValue: furnitureValueFt || '',
      },
      contact: {
        name,
        phone,
        email,
      },
      estimate: {
        estimatedTotal: price.delivery.custom ? 'Egyedi ajánlat' : formatFt(estimatedNetForPayload),
        estimatedTotalHuf: Math.round(estimatedNetForPayload),
        customQuote: price.delivery.custom,
      },
      pricingSnapshot: pricingSnapshot(effectivePricing),
      attachmentNames,
      attachmentUploads,
    };

    setSubmitPending(true);
    try {
      const data = await postQuoteInquiry({ payload });
      setSubmitEmailWarning(emailWarningsFromInquiryResponse(data));
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error?.name === 'AbortError') {
        setStepError('A küldés túl sokáig tartott. Kérjük, próbáld újra.');
      } else {
        setStepError(error.message || 'Nem sikerült elküldeni az árajánlatkérést.');
      }
    } finally {
      setSubmitPending(false);
    }
  };

  const onInvoiceFile = (file) => {
    setInvoiceFile(file);
    const err = validateFile(file);
    setInvoiceErr(err || '');
  };

  const onOrderInfoFile = (file) => {
    setOrderInfoFile(file);
    const err = validateFile(file);
    setOrderErr(err || '');
  };

  const onExtraFile1 = (file) => {
    setExtraFile1(file);
    const err = validateFile(file);
    setExtraErr1(err || '');
  };

  const onExtraFile2 = (file) => {
    setExtraFile2(file);
    const err = validateFile(file);
    setExtraErr2(err || '');
  };

  if (submitted) {
    return (
      <main className="qf-page">
        <div className="qf-inner">
          <QuoteRequestSuccess
            title="Köszönjük az ajánlatkérést!"
            subtitle="Az adataidat sikeresen rögzítettük. Munkatársunk hamarosan felveszi veled a kapcsolatot a megadott elérhetőségeken."
            banner={
              submitEmailWarning ? (
                <div className="qf-banner qf-banner--warn" role="status">
                  {submitEmailWarning}
                </div>
              ) : null
            }
            primary={
              <QuoteSuccessPrimaryLink to="/szolgaltatas/ikea-hazhozszallitas">
                Vissza az IKEA oldalra
              </QuoteSuccessPrimaryLink>
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main className="qf-page">
      <div className="qf-inner">
        <div className="qf-head">
          <nav className="svc-breadcrumb-trail qf-breadcrumb-trail" aria-label="Útvonal">
            <Link className="svc-breadcrumb-seg" to="/" state={{ scrollTo: 'szolgaltatas' }}>
              Szolgáltatások
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <Link className="svc-breadcrumb-seg" to="/szolgaltatas/ikea-hazhozszallitas">
              IKEA házhozszállítás
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="svc-breadcrumb-current" aria-current="page">
              Árajánlatkérés
            </span>
          </nav>

          <p className="qf-kicker">Ajánlatkérés bútorszállításhoz</p>
          <h1 className="qf-title">Szállítási Zónaárak (Körméreten belüli termékekre)</h1>
          <p className="qf-sub">
            {step + 1}. lépés / {STEPS.length} — {STEPS[step].title}
          </p>
        </div>

        <form className="qf-form" onSubmit={(e) => e.preventDefault()}>
          <div className="qf-progress" aria-hidden="true">
            {STEPS.map((s, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={s.id}
                className={`qf-progress-seg${i <= step ? ' qf-progress-seg--active' : ''}`}
              />
            ))}
          </div>

          {stepError && (
            <div className="qf-banner qf-banner--error" role="alert">
              {stepError}
            </div>
          )}

          {step === 0 && (
            <div className="qf-step">
              <h2 className="qf-step-title">ZÓNÁK ÉS FELÁR</h2>
              <div className="field qf-field">
                <label htmlFor="ikeaZone">Zónák *</label>
                <select id="ikeaZone" value={zone} onChange={(e) => setZone(e.target.value)}>
                  <option value="">Kérjük válasszon</option>
                  {zoneOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field qf-field">
                <label htmlFor="ikeaWeightKg">Szállítandó bútor súlya (kg) *</label>
                <input
                  id="ikeaWeightKg"
                  className={invalidFieldId === 'ikeaWeightKg' ? 'qf-invalid' : ''}
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="pl. 45"
                  value={furnitureWeightKg}
                  onChange={(e) => setFurnitureWeightKg(filterWeightInput(e.target.value))}
                />
              </div>

              <fieldset className="qf-fieldset qf-fieldset--plain">
                <legend>Szállítmány érték felár *</legend>
                {valueSurchargeOptions.map((opt) => (
                  <label key={opt.value} className="qf-radio-block">
                    <input
                      type="radio"
                      name="ikeaValueSurcharge"
                      value={opt.value}
                      checked={valueSurchargeValue === opt.value}
                      onChange={() => setValueSurchargeValue(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="qf-fieldset qf-fieldset--plain">
                <legend>Üzleten belüli összegyűjtés árai (nettó) *</legend>
                {collectionOptions.map((opt) => (
                  <label key={opt.value} className="qf-radio-block">
                    <input
                      type="radio"
                      name="ikeaCollection"
                      value={opt.value}
                      checked={collectionValue === opt.value}
                      onChange={() => setCollectionValue(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </fieldset>

              <div className="field qf-field">
                <label htmlFor="ikeaTotal">Összesen (nettó)</label>
                <input id="ikeaTotal" value={totalDisplay} readOnly />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="qf-step">
              <h2 className="qf-step-title">KÉRJEN ÁRAJÁNLATOT (IKEA BÚTORSZÁLLÍTÁS AJÁNLATKÉRÉS)</h2>

              <fieldset className="qf-fieldset">
                <legend>MELYIK ÁRUHÁZBÓL:</legend>
                <div className="field qf-field qf-field--full">
                  <label htmlFor="ikeaStore">Áruház *</label>
                  <select
                    id="ikeaStore"
                    className={invalidFieldId === 'ikeaStore' ? 'qf-invalid' : ''}
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                  >
                    {STORE_OPTIONS.map((o) => (
                      <option key={o.value || 'empty'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {price.budaors > 0 ? (
                    <small className="qf-help">Budaörsi áruházból: +{formatEstimateFt(price.budaors)} nettó.</small>
                  ) : null}
                  <small className="qf-help">
                    Jelenlegi nettó becslés: {totalDisplay}
                  </small>
                </div>
              </fieldset>

              <fieldset className="qf-fieldset">
                <legend>HOVÁ:</legend>
                <div className="qf-grid">
                  <GoogleAddressAutocomplete
                    id="ikeaToAddressSearch"
                    value={toAddressSearch}
                    onResolved={(addr) => {
                      if (addr.formatted !== undefined) setToAddressSearch(addr.formatted);
                      setToZip(addr.zip || '');
                      setToCity(addr.city || '');
                    }}
                  />
                  {STORE_ROUTE_ORIGIN_ADDRESS[store] ? (
                    <>
                      {routeDistanceLoading && (
                        <small className="qf-help qf-field--full">Távolság számítása folyamatban…</small>
                      )}
                      {!routeDistanceLoading && !routeDistanceError && Number(routeDistanceKm) > 0 && (
                        <small className="qf-help qf-field--full">
                          Becsült vezetési távolság (áruház → cél): {routeDistanceKm} km
                        </small>
                      )}
                      {!!routeDistanceError && (
                        <small className="qf-help qf-help--warn qf-field--full">{routeDistanceError}</small>
                      )}
                    </>
                  ) : null}
                  <div className="field qf-field">
                    <label htmlFor="toZip">Irányítószám *</label>
                    <input
                      id="toZip"
                      className={invalidFieldId === 'toZip' ? 'qf-invalid' : ''}
                      value={toZip}
                      readOnly
                    />
                  </div>
                  <div className="field qf-field">
                    <label htmlFor="toCity">Település *</label>
                    <input
                      id="toCity"
                      className={invalidFieldId === 'toCity' ? 'qf-invalid' : ''}
                      value={toCity}
                      readOnly
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="qf-fieldset">
                <legend>MIT?</legend>
                <div className="field qf-field qf-field--full">
                  <label htmlFor="furnitureValue">Szállítandó bútor értéke (bruttó) *</label>
                  <input
                    id="furnitureValue"
                    className={invalidFieldId === 'furnitureValue' ? 'qf-invalid' : ''}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="csak számjegyek (Ft)"
                    value={furnitureValueFt}
                    onChange={(e) => setFurnitureValueFt(filterHufDigitsInput(e.target.value))}
                  />
                </div>
                <div className="field qf-field qf-field--full">
                  <label htmlFor="furnitureParams">Szállítandó bútor paraméterei *</label>
                  <textarea
                    id="furnitureParams"
                    className={invalidFieldId === 'furnitureParams' ? 'qf-invalid' : ''}
                    rows={4}
                    value={furnitureParams}
                    onChange={(e) => setFurnitureParams(e.target.value)}
                  />
                </div>
                <div className="field qf-field qf-field--full">
                  <label htmlFor="ikeaNote2">Megjegyzés, fontos információ a szállításhoz</label>
                  <textarea id="ikeaNote2" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              </fieldset>

              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="invoiceFile">Számla feltöltése (max: {MAX_FILE_MB}Mb; Formátum: jpg, png, pdf)</label>
                  <input
                    id="invoiceFile"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => onInvoiceFile(e.target.files?.[0] ?? null)}
                  />
                  {invoiceErr && <span className="qf-file-err">{invoiceErr}</span>}
                  {invoiceFile && !invoiceErr && <span className="qf-file-name">{invoiceFile.name}</span>}
                </div>
                <div className="field qf-field">
                  <label htmlFor="orderInfoFile">Rendelési információk feltöltése</label>
                  <input
                    id="orderInfoFile"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => onOrderInfoFile(e.target.files?.[0] ?? null)}
                  />
                  {orderErr && <span className="qf-file-err">{orderErr}</span>}
                  {orderInfoFile && !orderErr && <span className="qf-file-name">{orderInfoFile.name}</span>}
                </div>
              </div>

              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="extraFile1">Egyéb, pl. meghatalmazás feltöltése</label>
                  <input
                    id="extraFile1"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => onExtraFile1(e.target.files?.[0] ?? null)}
                  />
                  {extraErr1 && <span className="qf-file-err">{extraErr1}</span>}
                  {extraFile1 && !extraErr1 && <span className="qf-file-name">{extraFile1.name}</span>}
                </div>
                <div className="field qf-field">
                  <label htmlFor="extraFile2">Egyéb, pl. meghatalmazás feltöltése</label>
                  <input
                    id="extraFile2"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => onExtraFile2(e.target.files?.[0] ?? null)}
                  />
                  {extraErr2 && <span className="qf-file-err">{extraErr2}</span>}
                  {extraFile2 && !extraErr2 && <span className="qf-file-name">{extraFile2.name}</span>}
                </div>
              </div>

              <fieldset className="qf-fieldset">
                <legend>MIKOR?</legend>
                <div className="field qf-field qf-field--full">
                  <label htmlFor="scheduleType">Felvétel típusa *</label>
                  <select
                    id="scheduleType"
                    className={invalidFieldId === 'scheduleType' ? 'qf-invalid' : ''}
                    value={scheduleType}
                    onChange={(e) => setScheduleType(e.target.value)}
                  >
                    <option value="flexible">Rugalmas (felvétel)</option>
                    <option value="fixed">Fix nap (felvétel)</option>
                  </select>
                </div>

                {scheduleType === 'flexible' && (
                  <div className="qf-date-row">
                    <div className="field qf-field">
                      <label htmlFor="flexFrom">Rugalmas (felvétel): -tól</label>
                      <input
                        id="flexFrom"
                        className={invalidFieldId === 'flexFrom' ? 'qf-invalid' : ''}
                        type="date"
                        value={flexFrom}
                        onChange={(e) => setFlexFrom(e.target.value)}
                      />
                    </div>
                    <div className="field qf-field">
                      <label htmlFor="flexTo">-ig</label>
                      <input
                        id="flexTo"
                        className={invalidFieldId === 'flexTo' ? 'qf-invalid' : ''}
                        type="date"
                        value={flexTo}
                        onChange={(e) => setFlexTo(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {scheduleType === 'fixed' && (
                  <div className="field qf-field">
                    <label htmlFor="fixDate">Fix nap (felvétel):</label>
                    <input
                      id="fixDate"
                      className={invalidFieldId === 'fixDate' ? 'qf-invalid' : ''}
                      type="date"
                      value={fixDate}
                      onChange={(e) => setFixDate(e.target.value)}
                    />
                  </div>
                )}
              </fieldset>
            </div>
          )}

          {step === 2 && (
            <div className="qf-step">
              <h2 className="qf-step-title">EGYÉB ADATOK</h2>
              <div className="qf-grid">
                <fieldset className="qf-fieldset qf-fieldset--plain">
                  <legend>Biztosítást igényel?*</legend>
                  <div className="qf-inline-radios">
                    <label className="qf-radio-inline">
                      <input
                        type="radio"
                        name="insurance"
                        value="igen"
                        checked={insurance === 'igen'}
                        onChange={() => setInsurance('igen')}
                      />
                      igen
                    </label>
                    <label className="qf-radio-inline">
                      <input
                        type="radio"
                        name="insurance"
                        value="nem"
                        checked={insurance === 'nem'}
                        onChange={() => setInsurance('nem')}
                      />
                      nem
                    </label>
                  </div>
                </fieldset>

                <fieldset className="qf-fieldset qf-fieldset--plain">
                  <legend>Fizetési mód*</legend>
                  <div className="qf-inline-radios">
                    <label className="qf-radio-inline">
                      <input
                        type="radio"
                        name="paymentWhen"
                        value="elore"
                        checked={paymentWhen === 'elore'}
                        onChange={() => setPaymentWhen('elore')}
                      />
                      előreutalás
                    </label>
                    <label className="qf-radio-inline">
                      <input
                        type="radio"
                        name="paymentWhen"
                        value="keszpenz"
                        checked={paymentWhen === 'keszpenz'}
                        onChange={() => setPaymentWhen('keszpenz')}
                      />
                      készpénz
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="qf-step">
              <h2 className="qf-step-title">AJÁNLATKÉRŐ ADATAI</h2>
              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="ikeaName">Név *</label>
                  <input id="ikeaName" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="field qf-field">
                  <label htmlFor="ikeaPhone">Telefon *</label>
                  <input
                    id="ikeaPhone"
                    type="tel"
                    placeholder={PHONE_PLACEHOLDER}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="ikeaEmail">E-mail cím *</label>
                  <input
                    id="ikeaEmail"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="ikeaEmailConfirm">E-mail cím megerősítése *</label>
                  <input
                    id="ikeaEmailConfirm"
                    type="email"
                    autoComplete="email"
                    value={emailConfirm}
                    onChange={(e) => setEmailConfirm(e.target.value)}
                  />
                </div>
              </div>

              <GdprConsentCheckbox
                id="ikea-gdpr"
                checked={privacyAccepted}
                onChange={setPrivacyAccepted}
              />
            </div>
          )}

          <div className="qf-actions">
            {step > 0 ? (
              <button type="button" className="secondary-btn qf-btn-back" onClick={back}>
                Vissza
              </button>
            ) : (
              <Link className="secondary-btn qf-btn-back" to="/szolgaltatas/ikea-hazhozszallitas">
                Vissza
              </Link>
            )}

            {step < STEPS.length - 1 ? (
              <button type="button" className="primary-btn" onClick={next}>
                Tovább
              </button>
            ) : (
              <button type="button" className="primary-btn" disabled={submitPending} onClick={handleSubmit}>
                {submitPending ? 'Küldés...' : 'Beküldés'}
              </button>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}

